import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useDebounce } from '@uidotdev/usehooks';
import i18n from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DialogStyled, RelativelyPositionedSearchFilter, Table } from '@/components';
import { DEBOUNCE_TIME_MS } from '@/constants';
import {
  getContentLanguage,
  getSavedCurrentPage,
  getSavedRowsPerPage,
  handleActivateItems,
  handleDeactivateItems,
  handleDeleteItems,
  handleDuplicateItems,
  languageSchemaRequired,
  languageSortComparator,
  onSelectAll,
  onSelectRow,
  Tag,
  Tags,
  useTag,
  useTags,
} from '@/core';
import { DefaultCell, IsActiveStatusCell } from '@/core/renderCells';
import {
  FiltersDialog,
  FiltersTableHeader,
  FilterTypes,
  PageHeader,
  prepareTagToPost,
} from '@/features';

const schema = z.object({
  name: languageSchemaRequired,
  showImmediately: z.boolean(),
});

const findRowById = (data: Tags, selectedRowId: number): Tag | undefined =>
  data.results.find((row) => row.id === selectedRowId);

const OFFER_PAGE_KEY = 'filters';

export const PageFilters = () => {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Tag | undefined>(undefined);
  const [actionType, setActionType] = useState<'EDIT' | 'ADD'>('ADD');

  //pagination states
  const [currentPage, setCurrentPage] = useState<number>(getSavedCurrentPage(OFFER_PAGE_KEY));
  const [rowsPerPage, setRowsPerPage] = useState<number>(getSavedRowsPerPage(OFFER_PAGE_KEY));

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const debouncedSearchInputValue = useDebounce(searchInputValue, DEBOUNCE_TIME_MS);

  const {
    mutations: {
      createMutation,
      updateMutation,
      deleteMutation,
      duplicateMutation,
      activateMutation,
      deactivateMutation,
    },
  } = useTag();
  const {
    useTagsQuery: { data, status, refetch },
    useTagsSearch,
  } = useTags({ page: currentPage, limit: rowsPerPage });

  const { data: tagsFilteredByName, status: tagsFilteredByNameStatus } =
    useTagsSearch(debouncedSearchInputValue);

  const isFilterActive = debouncedSearchInputValue.length > 0;
  const displayRows = isFilterActive ? tagsFilteredByName?.results || [] : data?.results || [];
  const displayTotal = isFilterActive
    ? tagsFilteredByName?.total_results || 0
    : data?.total_results || 0;
  const paginationTotal = isFilterActive
    ? tagsFilteredByName?.total_results || (debouncedSearchInputValue ? 1 : 0)
    : data?.total_results || 0;

  const methods = useForm<FilterTypes>({
    defaultValues: {
      name: { en: '', cs: '', de: '' },
      showImmediately: false,
    },
    resolver: zodResolver(schema),
  });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  const noErrors = !Object.values(errors).some((error) => error);

  const handleChangeModalState = (state: boolean) => {
    if (!state) {
      methods.reset();
    }
    setIsModalOpen(state);
  };

  const handleCancelAction = () => {
    setSelectedRows([]);
  };

  const onHeaderButtonClick = () => {
    methods.reset();
    setActionType('ADD');
    handleChangeModalState(true);
  };

  const onRowEdit = (selectedRowId: number) => {
    if (!data) return;
    const selectedRow = findRowById(data, selectedRowId);
    if (!selectedRow) return;
    methods.setValue('name', selectedRow.name);
    methods.setValue('showImmediately', selectedRow.is_active);
    setActionType('EDIT');
    setSelectedRowToEdit(selectedRow);
    handleChangeModalState(true);
  };

  const onSubmit = async (data: FilterTypes) => {
    if (actionType === 'ADD') {
      await createMutation.mutateAsync(
        prepareTagToPost({
          name: data.name,
          showImmediately: data.showImmediately,
        }),
      );
    } else {
      if (!selectedRowToEdit) return;
      await updateMutation.mutateAsync({
        id: selectedRowToEdit.id,
        ...prepareTagToPost({
          name: data.name,
          showImmediately: data.showImmediately,
        }),
      });
    }
    handleChangeModalState(false);
    methods.reset();
    void refetch();
  };

  const handleDeleteTags = async () => {
    await handleDeleteItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deleteMutation,
    });
  };

  const handleActivateTags = async () => {
    await handleActivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      activateMutation,
    });
    setSelectedRowToEdit(undefined);
  };

  const handleDeactivateTags = async () => {
    await handleDeactivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deactivateMutation,
    });
    setSelectedRowToEdit(undefined);
  };

  const handleDuplicateTags = async () => {
    await handleDuplicateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      duplicateMutation,
    });
    setSelectedRowToEdit(undefined);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('HEADERS.NAME'),
      sortComparator: languageSortComparator,
      flex: 0.2,
      renderCell: (params) => (
        <DefaultCell value={params.value[getContentLanguage(i18n.language)]} />
      ),
    },
    {
      field: 'is_active',
      flex: 1,
      headerName: t('HEADERS.STATUS'),
      renderCell: (params) => <IsActiveStatusCell status={params.value} />,
    },
  ];

  return (
    <Stack>
      <PageHeader
        onButtonClick={onHeaderButtonClick}
        actionButtonLabel={t('ACTIONS.ADD_FILTER_LABEL')}
      />
      <RelativelyPositionedSearchFilter
        inputValue={searchInputValue}
        setInputValue={setSearchInputValue}
      />
      <Table
        key={`${searchInputValue}_${currentPage}_${rowsPerPage}_${JSON.stringify(data)}_${JSON.stringify(tagsFilteredByName)}`}
        rows={displayRows}
        columns={columns}
        tableId={OFFER_PAGE_KEY}
        isDraggable={false}
        isCheckBox
        isRowsEditable
        loading={
          status === 'pending' ||
          createMutation.status === 'pending' ||
          updateMutation.status === 'pending' ||
          deleteMutation.status === 'pending' ||
          tagsFilteredByNameStatus === 'pending'
        }
        onEditRow={onRowEdit}
        selectedRows={selectedRows}
        onSelectRow={(selectedRowId) => onSelectRow(selectedRowId, setSelectedRows)}
        onSelectAll={() => onSelectAll(data, selectedRows, setSelectedRows)}
        pagination
        paginationOptions={{
          currentPage,
          rowsPerPage,
          totalRows: paginationTotal,
          rowCount: paginationTotal,
          rowsPerPageOptions: [15, 25, 50, 100],
          onPageSizeChange: (newPageSize: number) => {
            setCurrentPage(0);
            setRowsPerPage(newPageSize);
          },
          onPageChange: (newPage: number) => {
            setCurrentPage(newPage);
          },
          pageSize: rowsPerPage,
          page: currentPage,
        }}
        ToolBar={(props) => (
          <FiltersTableHeader
            {...props}
            count={displayTotal}
            selectedRows={selectedRows}
            onDelete={handleDeleteTags}
            onCancel={handleCancelAction}
            onDuplicate={handleDuplicateTags}
            onDeactivate={handleDeactivateTags}
            onActivate={handleActivateTags}
          />
        )}
      />
      <FormProvider {...methods}>
        <DialogStyled
          open={isModalOpen}
          title={
            actionType === 'ADD' ? t('DIALOG_TITLE.FILTERS.ADD') : t('DIALOG_TITLE.FILTERS.EDIT')
          }
          sx={{ '&& .MuiPaper-root': { width: '100%', maxWidth: '500px' } }}
          setOpen={handleChangeModalState}
          onActionButtonClick={handleSubmit(onSubmit)}
          actionButtonLabel={actionType === 'ADD' ? t('ACTIONS.ADD') : t('ACTIONS.EDIT')}
          isCorrectlyFilledOut={noErrors}
        >
          <FiltersDialog />
        </DialogStyled>
      </FormProvider>
    </Stack>
  );
};
